import { uploadImage } from "pages/MarketingPages/pagesApi";

/**
 * The pre-save safety net for inline images.
 *
 * The toolbar, a drop and a paste of an image *file* all upload through
 * `components/RichTextEditor/imageUpload`, so they never produce base64. What
 * still can is a paste of *markup* that carries `data:` sources — copying a
 * block out of a mail client or another editor. The API sanitizer drops `data:`
 * by scheme, so without this pass such an image would survive in the editor and
 * silently vanish from the stored body — the exact class of silent failure
 * DAB-824 was about.
 */

/** `data:<mime>;base64,<payload>` → File the upload endpoint accepts. */
const dataUrlToFile = (dataUrl, index) => {
  const match = /^data:([^;,]+);base64,(.*)$/s.exec(dataUrl);
  if (!match) return null;
  const [, mime, base64] = match;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const extension = (mime.split("/")[1] || "png").replace(/[^a-z0-9]/gi, "");
  return new File([bytes], `pasted-${Date.now()}-${index}.${extension}`, { type: mime });
};

/**
 * Replace every base64-embedded image in `html` with an uploaded URL.
 *
 * Throws on the first failed upload rather than dropping the image: the
 * caller aborts the save and the editor still holds everything, which beats
 * storing a body the sanitizer would quietly amputate.
 */
export const uploadDataImages = async (html, upload = uploadImage) => {
  if (!html || html.indexOf("data:") === -1) return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const embedded = Array.from(doc.querySelectorAll('img[src^="data:"]'));
  if (embedded.length === 0) return html;

  for (let i = 0; i < embedded.length; i += 1) {
    const img = embedded[i];
    const file = dataUrlToFile(img.getAttribute("src"), i);
    if (!file) {
      // Not base64 (e.g. an SVG data URI) — the sanitizer will drop it, and
      // there is nothing meaningful to upload. Remove it here so the author
      // sees the loss in the editor instead of discovering it on the site.
      img.remove();
      continue;
    }
    const url = await upload(file);
    if (!url) throw new Error("Качването не върна URL адрес");
    img.setAttribute("src", url);
  }

  return doc.body.innerHTML;
};
